import { useMemo, useState } from 'react'
import { DEALS_COL, DealClientCell, DealPropertyCell, DealSortHead, formatDealDate, formatMonthlyPrice } from '@/components/deals-table'
import { platformLogo } from '@/lib/platforms'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Redo } from 'lucide-react'
import {
	Badge,
	Button,
	IconButton,
	PanelFilters,
	SearchInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
	Tabs, TabsList, TabsTrigger,
} from '@rentflowdev/ui'
import { useTranslation } from 'react-i18next'
import { useListContactsQuery, useListDealsQuery, useListPropertiesQuery, useUpdateDealStageMutation, useUpdateDealStatusMutation } from '@/api'
import { disambiguatePropertyLabels } from '@/lib/property-options'
import type { Contact, Deal, DealStatus } from '@/types'
import { DEAL_STATUS_KEYS } from '@/types'
import { PanelDown } from '@/components/panel-down'

/** UI-augmented Deal — adds frontend-only display fields not yet on the backend. */
type EnrichedDeal = Deal & {
	propertyTitle?: string
	propertyAddress?: string
	propertyImage?: string
	propertyPrice?: string
	clientName?: string
}


export const Route = createFileRoute('/_app/deals/completed')({
	component: DealsCompletedPage,
})

const PAGE_SIZE = 10

function downloadCsv(contacts: Contact[], filename: string) {
	const header = 'Name,Phone,Email,Type'
	const rows = contacts.map((c) =>
		[c.name, c.phone ?? '', c.email ?? '', c.type].map((v) => `"${v.replace(/"/g, '""')}"`).join(',')
	)
	const csv = [header, ...rows].join('\n')
	const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = filename
	a.click()
	URL.revokeObjectURL(url)
}

// CLOSED and COMPLETED both mean the deal ended in a tenancy — the tab filter
// below already groups them — so they read and colour the same. Without this,
// CLOSED fell through and the badge printed the raw enum.
const RENTED_STATUSES: DealStatus[] = ['COMPLETED', 'CLOSED']

function statusBadgeVariant(status: DealStatus): 'success' | 'destructive' | 'secondary' {
	if (RENTED_STATUSES.includes(status)) return 'success'
	if (status === 'CANCELLED') return 'destructive'
	return 'secondary'
}

function statusLabel(status: DealStatus, t: (key: string) => string): string {
	if (RENTED_STATUSES.includes(status)) return t('deals.completed.rented')
	if (status === 'CANCELLED') return t('deals.completed.rejected')
	// Any status added to the enum later shows its translated name rather than
	// the raw value — which is what let CLOSED slip through unnoticed.
	return t(DEAL_STATUS_KEYS[status] ?? status)
}

type SortField = 'property' | 'client' | 'price' | 'date'
type SortDir   = 'asc' | 'desc'

function DealsCompletedPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [tab,       setTab]       = useState('all')
	const [search,    setSearch]    = useState('')
	const [page,      setPage]      = useState(1)
	const [sortField, setSortField] = useState<SortField>('property')
	const [sortDir,   setSortDir]   = useState<SortDir>('asc')
	const [filterProperty, setFilterProperty] = useState('')
	const [filterClient,   setFilterClient]   = useState('')
	const [filterSource,   setFilterSource]   = useState('')

	function toggleSort(field: SortField) {
		if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
		else { setSortField(field); setSortDir('asc') }
	}

	const { data: completedData, isLoading: loadingCompleted } = useListDealsQuery({ status: 'COMPLETED', limit: 100 })
	const { data: closedData,    isLoading: loadingClosed }    = useListDealsQuery({ status: 'CLOSED',    limit: 100 })
	const { data: cancelledData, isLoading: loadingCancelled } = useListDealsQuery({ status: 'CANCELLED', limit: 100 })
	const { data: contactsData } = useListContactsQuery()
	// Deals only carry the property's address, so two properties at one address
	// are indistinguishable in the filter. Type and status come from here.
	const { data: propertiesData } = useListPropertiesQuery({ limit: 200 })
	const [updateStage]      = useUpdateDealStageMutation()
	const [updateDealStatus] = useUpdateDealStatusMutation()

	const isLoading = loadingCompleted || loadingClosed || loadingCancelled

	// Deal.propertyTitle/Image/Address/Price are expected from the backend.
	const deals: EnrichedDeal[] = useMemo(
		() => [
			...(completedData?.items ?? []),
			...(closedData?.items    ?? []),
			...(cancelledData?.items ?? []),
		],
		[completedData, closedData, cancelledData],
	)

	const propertyFacts = useMemo(
		() => new Map((propertiesData?.properties ?? []).map((p) => [p.id, { type: p.type, status: p.status }])),
		[propertiesData]
	)

	const filterOptions = useMemo(() => {
		const properties = new Map<string, string>()
		const clients    = new Map<string, string>()
		const sources    = new Set<string>()
		for (const d of deals) {
			if (d.propertyId && d.propertyTitle) properties.set(d.propertyId, d.propertyTitle)
			if (d.contactId  && d.clientName)    clients.set(d.contactId, d.clientName)
			if (d.source) sources.add(d.source)
		}
		return { properties: disambiguatePropertyLabels(properties, propertyFacts, t), clients, sources: [...sources] }
	}, [deals, propertyFacts, t])

	const tabFiltered = tab === 'all'
		? deals
		: tab === 'rented'
			? deals.filter((d) => d.status === 'COMPLETED' || d.status === 'CLOSED')
			: deals.filter((d) => d.status === 'CANCELLED')

	const filtered = tabFiltered.filter((d) => {
		if (filterProperty && d.propertyId !== filterProperty) return false
		if (filterClient   && d.contactId  !== filterClient)   return false
		if (filterSource   && d.source     !== filterSource)   return false
		if (search) {
			const q = search.toLowerCase()
			return (d.propertyTitle?.toLowerCase().includes(q))
				|| (d.clientName?.toLowerCase().includes(q))
				|| d.id.toLowerCase().includes(q)
		}
		return true
	})

	const sorted = useMemo(() => [...filtered].sort((a, b) => {
		const dir = sortDir === 'asc' ? 1 : -1
		switch (sortField) {
			case 'property': return (a.propertyTitle ?? '').localeCompare(b.propertyTitle ?? '') * dir
			case 'client':   return (a.clientName ?? '').localeCompare(b.clientName ?? '') * dir
			case 'price':    return ((a.propertyPrice ?? '') > (b.propertyPrice ?? '') ? 1 : -1) * dir
			// По дате закрытия: в этой таблице хронология — «когда закрыли»,
			// а не «когда обратились». Ещё не проставленные уходят вниз.
			case 'date':     return ((a.closedAt ? new Date(a.closedAt).getTime() : 0) - (b.closedAt ? new Date(b.closedAt).getTime() : 0)) * dir
		}
	}), [filtered, sortField, sortDir])

	const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

	return (
		<div className="flex h-full flex-col">
			<PanelFilters
				filters={
					<>
						<Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1) }}>
							<TabsList variant="pills">
								<TabsTrigger variant="pills" value="all">{t('deals.completed.all')}</TabsTrigger>
								<TabsTrigger variant="pills" value="rented">{t('deals.completed.rented')}</TabsTrigger>
								<TabsTrigger variant="pills" value="rejected">{t('deals.completed.rejected')}</TabsTrigger>
							</TabsList>
						</Tabs>
						<Select
							value={filterProperty || 'all'}
							onValueChange={(v) => { setFilterProperty(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="lg" tone="muted">
								<SelectValue placeholder={t('deals.filterProperty')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('deals.allProperties')}</SelectItem>
								{[...filterOptions.properties.entries()].map(([id, title]) => (
									<SelectItem key={id} value={id}>{title}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={filterClient || 'all'}
							onValueChange={(v) => { setFilterClient(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="lg" tone="muted">
								<SelectValue placeholder={t('deals.filterClient')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('deals.allClients')}</SelectItem>
								{[...filterOptions.clients.entries()].map(([id, name]) => (
									<SelectItem key={id} value={id}>{name}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={filterSource || 'all'}
							onValueChange={(v) => { setFilterSource(v === 'all' ? '' : v); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('deals.completed.filterSource')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('deals.completed.allSources')}</SelectItem>
								{filterOptions.sources.map((s) => (
									<SelectItem key={s} value={s}>{s}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</>
				}
				actions={
					<>
						<SearchInput
							placeholder={t('deals.search')}
							value={search}
							className="w-[200px]"
							onChange={(e) => { setSearch(e.target.value); setPage(1) }}
						/>
						<Button
							size="sm"
							onClick={() => {
								const contacts = contactsData?.items ?? []
								if (contacts.length > 0) downloadCsv(contacts, 'contacts.csv')
							}}
						>
							{t('deals.completed.exportContacts')}
						</Button>
					</>
				}
			/>

			{isLoading ? (
				<div className="flex flex-1 items-center justify-center">
					<Spinner />
				</div>
			) : filtered.length === 0 ? (
				<div className="flex flex-1 items-center justify-center">
					<p className="text-sm text-(--color-muted-foreground)">{t('deals.emptyCompleted')}</p>
				</div>
			) : (
				<>
					<div className="flex min-h-0 flex-1 flex-col overflow-auto px-4">
						<div className="overflow-hidden rounded-lg border border-(--color-border)">
							<Table>
								<TableHeader>
									<TableRow className="hover:bg-transparent">
										<DealSortHead field="property" active={sortField === 'property'} dir={sortDir} onToggle={toggleSort}>
											{t('deals.table.property')}
										</DealSortHead>
										<DealSortHead field="client" className={DEALS_COL.client} active={sortField === 'client'} dir={sortDir} onToggle={toggleSort}>
											{t('deals.table.client')}
										</DealSortHead>
										<DealSortHead field="price" className={DEALS_COL.price} active={sortField === 'price'} dir={sortDir} onToggle={toggleSort}>
											{t('deals.table.price')}
										</DealSortHead>
										<TableHead className={`${DEALS_COL.source} px-4 py-3 font-[500] text-(--color-muted-foreground)`}>
											{t('deals.table.source')}
										</TableHead>
										<TableHead className={`${DEALS_COL.status} px-4 py-3 font-[500] text-(--color-muted-foreground)`}>
											{t('deals.table.status')}
										</TableHead>
										<DealSortHead field="date" className={DEALS_COL.date} active={sortField === 'date'} dir={sortDir} onToggle={toggleSort}>
											{t('deals.table.closedAt')}
										</DealSortHead>
										<TableHead className={DEALS_COL.action} />
									</TableRow>
								</TableHeader>
								<TableBody>
									{paged.map((deal) => (
										<TableRow
											key={deal.id}
											className="h-24 cursor-pointer"
											onClick={(e) => {
												if ((e.target as Element).closest('button,[role="button"]')) return
												navigate({ to: '/deals/$dealId', params: { dealId: deal.id } })
											}}
										>
											<DealPropertyCell
												image={deal.propertyImage}
												title={deal.propertyTitle ?? `Сделка №${deal.id.slice(-6)}`}
												address={deal.propertyAddress}
												onOpenProperty={deal.propertyId
													? () => void navigate({ to: '/properties/$propertyId', params: { propertyId: deal.propertyId! } })
													: undefined}
											/>
											<DealClientCell
												name={deal.clientName}
												phone={deal.clientPhone}
												email={deal.clientEmail}
												fallback={deal.contactId}
											/>
											<TableCell className={`${DEALS_COL.price} px-4 text-sm text-(--color-foreground)`}>
												{formatMonthlyPrice(deal) ?? '—'}
											</TableCell>
											<TableCell className={`${DEALS_COL.source} px-4`}>
												{deal.source && (platformLogo(deal.source)
													? <Badge variant="logo" size="sm" logoSrc={platformLogo(deal.source)} logoAlt={deal.source} />
													: <Badge variant="secondary" size="sm">{deal.source}</Badge>
												)}
											</TableCell>
											<TableCell className={`${DEALS_COL.status} px-4`}>
												<Badge variant={statusBadgeVariant(deal.status)} size="sm">
													{statusLabel(deal.status, t)}
												</Badge>
											</TableCell>
											<TableCell className={`${DEALS_COL.date} px-4 text-sm text-(--color-muted-foreground)`}>
												{deal.closedAt ? formatDealDate(deal.closedAt) : '—'}
											</TableCell>
											<TableCell className={`${DEALS_COL.action} px-4`}>
												{/* Одна кнопка на строку. Рядом стоял ещё «домик», и
												    две соседние иконки делали разное — теперь объект
												    открывается по адресу, а здесь остаётся возврат. */}
												<IconButton
													variant="ghost"
													size="sm"
													aria-label={t('deals.completed.retry')}
													onClick={() => {
														void updateStage({ id: deal.id, body: { stage: 'NEW' } })
														void updateDealStatus({ id: deal.id, body: { status: 'ACTIVE' } })
													}}
												>
													<Redo className="size-4" />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</div>
					<PanelDown total={filtered.length} page={page} onPageChange={setPage} limit={PAGE_SIZE} />
				</>
			)}
		</div>
	)
}
